import { NavLink } from 'react-router-dom';
import { Home, CheckSquare, Heart, Sparkles, FolderTree, Users, Search, User, Settings, LogOut, Target, Goal, FileText, X, Grid3x3, Mic } from 'lucide-react';
import clsx from 'clsx';
import { useAuth } from '@/auth/auth-context';
import { useDemoContext } from '@/demo/demo-context';

const navItems = [
  { to: '/home', label: 'Home', icon: Home },
  { to: '/values', label: 'Values', icon: Heart },
  { to: '/goals', label: 'Goals', icon: Goal },
  { to: '/bingo', label: 'Goal Bingo', icon: Grid3x3 },
  { to: '/entries', label: 'Entries', icon: FileText },
  { to: '/voice-capture', label: 'Voice Capture', icon: Mic },
  { to: '/tasks', label: 'Tasks', icon: CheckSquare },
  { to: '/focus', label: 'Focus', icon: Target },
  { to: '/review', label: 'Priority Review', icon: Sparkles },
  { to: '/topics', label: 'Topics', icon: FolderTree },
  { to: '/assignees', label: 'Assignees', icon: Users },
  { to: '/search', label: 'Search', icon: Search },
  { to: '/profile', label: 'Profile', icon: User },
  { to: '/settings', label: 'Settings', icon: Settings },
];

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  const { user, signOut } = useAuth();
  const { getDemoPath } = useDemoContext();

  const handleSignOut = () => {
    signOut();
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <aside
        className={clsx(
          'fixed top-0 left-0 bottom-0 w-64 bg-stone-50 border-r border-stone-200 z-50 transform transition-transform duration-300 ease-in-out lg:hidden',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Header with logo and close button */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-stone-200 bg-primary-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-800 rounded-xl flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 18l5-8 3 4 4-6 4 10H4z" />
                  <circle cx="17" cy="7" r="2" fill="currentColor" opacity="0.8" />
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-display font-semibold text-white leading-tight tracking-tight">Find My Way</span>
                <span className="text-xs text-primary-200 leading-tight">Choose courage daily</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-white hover:bg-primary-600 transition-colors"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={getDemoPath(item.to)}
                onClick={onClose}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
                    isActive
                      ? 'bg-primary-100 text-primary-800 shadow-sm'
                      : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'
                  )
                }
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* User info and sign out */}
          <div className="px-4 py-4 border-t border-stone-200 bg-stone-100/50">
            {user && (
              <div className="flex items-center gap-3 mb-3">
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-10 h-10 rounded-full object-cover ring-2 ring-primary-200"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center ring-2 ring-primary-200">
                    <User className="w-5 h-5 text-primary-700" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-900 truncate">{user.name}</p>
                  <p className="text-xs text-stone-500 truncate">{user.email}</p>
                </div>
              </div>
            )}
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-stone-600 hover:bg-stone-200 hover:text-stone-900 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
            <p className="text-xs text-stone-400 mt-3 px-3">Find My Way v1.2.0</p>
          </div>
        </div>
      </aside>
    </>
  );
}

